import { useRef, useCallback } from 'react';
import { TextInputField } from 'modules/templateEditor/common/components';
import { InAppContentDefinition } from 'modules/graphql/types';
import { FormField } from 'ui/formField';
import { getIsEditMode, constructFieldErrors, hasHandlebars } from '../../../common/utils';
import { constructErrorInlineText } from '../../../common/ErrorInlineText';
import { FieldNames } from '../../../common/types';
import { useQueryParamContext } from '../../../context/QueryParamContext';
import { useEditableFieldsContext } from '../../../context/EditableFieldsContext';
import { SETTINGS_DISPLAY_CONTENT } from '../../constants';
import { useErrorValidationContext } from '../../../context/ErrorValidationContext';
import { getRokuRestrictionText } from '../../utils';

export const RokuHeader = () => {
  const { view } = useQueryParamContext();
  const { isEditMode, liveTemplate, setCurrentMediumField } = useEditableFieldsContext();
  const { localeContentErrors, localeContentWarnings } = useErrorValidationContext();
  const { ottPayload } = (liveTemplate?.mediumData || {}) as InAppContentDefinition;

  const headerHasHandlebars = hasHandlebars(ottPayload?.dialogHeader);

  const isEditModeVisible = getIsEditMode(view, isEditMode);
  const titleRef = useRef<HTMLInputElement | null>(null);

  const mediumDataValue = useCallback(() => {
    if (ottPayload?.dialogHeader) return ottPayload.dialogHeader;
    if (!isEditModeVisible) return SETTINGS_DISPLAY_CONTENT.roku.title.unset;
    return '';
  }, [isEditModeVisible, ottPayload?.dialogHeader]);

  const { hasError: hasTitleError, hasWarning: hasTitleWarning } = constructFieldErrors({
    contentErrors: localeContentErrors,
    contentWarnings: localeContentWarnings,
    fieldName: FieldNames.ROKU_TITLE,
  });

  const titleErrorText = constructErrorInlineText({
    condition: isEditModeVisible,
    hasError: hasTitleError,
    hasWarning: hasTitleWarning,
  });

  return (
    <FormField
      isHorizontalLayout
      hasError={hasTitleError}
      hasWarning={hasTitleWarning}
      errorText={titleErrorText}
      labelText={SETTINGS_DISPLAY_CONTENT.roku.title.label}
      restrictionText={getRokuRestrictionText(ottPayload?.dialogHeader, isEditModeVisible)}
      data-test="form-field-roku-header"
    >
      <TextInputField
        hasError={hasTitleError}
        hasWarning={hasTitleWarning}
        mediumRef={titleRef}
        mediumDataKey={'ottPayload.dialogHeader'}
        mediumDataValue={mediumDataValue()}
        changeHandler={(title) =>
          setCurrentMediumField('ottPayload', { ...ottPayload, dialogHeader: title })
        }
        maxLength={headerHasHandlebars ? undefined : 100}
        showSnippet
      />
    </FormField>
  );
};
